(() => {
  class TieredAndSingleDiscountCode {
    constructor() {
      this.tdcData = window.ShopData?.settings?.TDC_DP_data || {};
      this.sdcData = window.ShopData?.settings?.SDC_DP_data || {};
      this.priceSymbol = window.ShopData?.country?.currency?.symbol || '$';
      this.collectionTurnOffTieredDiscount = window.ShopData?.collection?.metafields?.custom?.turn_off_tiered_discount;
      this.collectionTurnOffSearchspring = window.ShopData?.collection?.metafields?.custom?.turn_off_searchspring || !window.ShopData?.settings?.ss_enable;
      this.membershipPrice = this.sdcData?.SDC_DP_enable
        ? this.sdcData?.SDC_DP_membership_price
        : this.tdcData?.TDC_DP_membership_price;

      this.cartTotal = 0;
      this.lastCartData = null;
      this.cartListenersBound = false;
      this.observers = new Map();
      this.initialized = false;
      this.earlyApplyAttempts = 0;
      this.maxEarlyApplyAttempts = 6;
      this.collectionApplyScheduled = false;
      this.cartRequest = null;
      this.cartDataRequest = null;
      this.products = Array.isArray(window.globalProductObject) ? window.globalProductObject : [];
      this.productIndex = this.buildProductIndex();
      this.sdcInclusionTags = this.parseTagList(window.ShopData?.settings?.SDC_DP_inclusion_tag);
      this.unlockDiscountExcludingTags = this.parseTagList(window.ShopData?.settings?.unlockDiscountExcludingTags);
      this.ssContainerObserver = null;
      this.ssCardsObserver = null;
      this.ssContainerEl = null;
      this.ssInitTimeout = null;
      this.ssPending = false;
      this.ssInitialized = false;

      this.initOnReady();
      this.tryEarlyApply();
    }

    initOnReady() {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.init(), { once: true });
      } else {
        this.init();
      }
    }

    init() {
      if (this.initialized) return;
      if (!this.isDiscountEnabled()) return;

      this.initialized = true;
      this.productIndex = this.buildProductIndex();
      // Start listening to cart updates and pre-fill cart total.
      this.bindCartListeners();
      this.cartTotal = this.getInitialCartTotalFromNodes();
      this.applyDiscounts();
      this.fetchCartData().then(() => this.applyDiscounts());
      this.startSearchspring();
    }

    tryEarlyApply() {
      if (!this.isDiscountEnabled()) return;
      const hasNodes = document.querySelector('.discount-with-code');
      if (hasNodes) {
        this.cartTotal = this.getInitialCartTotalFromNodes();
        this.applyDiscounts();
        return;
      }

      if (this.earlyApplyAttempts >= this.maxEarlyApplyAttempts) return;

      this.earlyApplyAttempts += 1;
      requestAnimationFrame(() => this.tryEarlyApply());
    }

    getInitialCartTotalFromNodes() {
      const node = document.querySelector('.discount-with-code[data-cart-total-price]');
      if (!node) return this.cartTotal;
      const raw = node.getAttribute('data-cart-total-price');
      const num = Number(raw);
      return Number.isNaN(num) ? this.cartTotal : num;
    }

    isDiscountEnabled() {
      const isTdcOn = Boolean(this.tdcData?.TDC_DP_enable);
      const isSdcOn = Boolean(this.sdcData?.SDC_DP_enable);
      if (!isTdcOn && !isSdcOn) return false;
      return this.collectionTurnOffTieredDiscount !== true;

    }

    bindCartListeners() {
      if (this.cartListenersBound) return;
      const handler = (event) => this.handleCartUpdate(event);
      ['cart:refresh', 'updateCart', 'line-item:change:end', 'cart:item-added'].forEach((evt) => {
        document.addEventListener(evt, handler);
      });
      this.cartListenersBound = true;
    }

    async handleCartUpdate(event) {
      const cartData = event?.detail?.cartData || event?.detail?.cart;
      if (cartData && typeof cartData.total_price === 'number') {
        this.lastCartData = cartData;
        this.cartTotal = cartData.total_price;
        this.applyDiscounts();
        return;
      }
      await this.fetchCartData();
      this.applyDiscounts();
    }

    async fetchCartTotal() {
      if (this.cartRequest) return this.cartRequest;
      this.cartRequest = fetch('/cart.js', {
        headers: {
          'Accept': 'application/json'
        }
      })
        .then((response) => (response.ok ? response.json() : null))
        .then((data) => {
          if (data && typeof data.total_price === 'number') {
            this.cartTotal = data.total_price;
          }
          return data;
        })
        .catch(() => null)
        .finally(() => {
          this.cartRequest = null;
        });

      return this.cartRequest;
    }

    async fetchCartData() {
      if (this.cartDataRequest) return this.cartDataRequest;
      this.cartDataRequest = fetch('/cart.js', {
        headers: {
          'Accept': 'application/json'
        }
      })
        .then((response) => (response.ok ? response.json() : null))
        .then((data) => {
          if (data && typeof data.total_price === 'number') {
            this.cartTotal = data.total_price;
            this.lastCartData = data;
          }
          return data;
        })
        .catch(() => null)
        .finally(() => {
          this.cartDataRequest = null;
        });

      return this.cartDataRequest;
    }

    applyDiscounts() {
      this.syncProducts();
      this.ensureSearchspringDiscountNodes();
      this.updateCartDrawerTieredDiscount();
      this.observePdpSwap();
      this.observeCollectionGrid();
      this.observeQuickView();
      const nodes = document.querySelectorAll('.discount-with-code');

      if (!nodes.length) return;

      // Update all discount blocks currently in DOM.
      nodes.forEach((node) => this.updateProductNode(node));
    }

    updateProductNode(node) {
      const priceRaw = node.getAttribute('data-p-price');
      const compareAtRaw = node.getAttribute('data-p-compare-at-price');
      const key = [
        priceRaw || '',
        compareAtRaw || '',
        String(this.cartTotal || 0),
        String(this.sdcData?.SDC_DP_enable || false),
        String(this.tdcData?.TDC_DP_enable || false),
        String(this.membershipPrice || 0),
      ].join('|');

      if (node.dataset.discountKey === key && node.classList.contains('discount-with-code--ready')) {
        return;
      }

      if (compareAtRaw) {
        const compareAtEl = node.querySelector('.discount-compare-at-price');
        if (compareAtEl) {
          const nextText = `${this.priceSymbol}${this.formatMoney(compareAtRaw)}`;
          if (compareAtEl.textContent !== nextText) {
            compareAtEl.textContent = nextText;
          }
        }
      }

      if (!priceRaw) return;

      const { finalPrice, discountCode } = this.getFinalPriceAndDiscountCode(Number(priceRaw));

      const priceEl = node.querySelector('.discount-price');
      if (priceEl && typeof finalPrice === 'number') {
        const nextText = `${this.priceSymbol}${finalPrice.toFixed(2)}`;
        if (priceEl.textContent !== nextText) {
          priceEl.textContent = nextText;
        }
      }

      const codeEl = node.querySelector('.discount-code');
      if (codeEl && discountCode) {
        if (codeEl.textContent !== discountCode) {
          codeEl.textContent = discountCode;
        }
      }

      node.dataset.discountKey = key;
      node.classList.add('discount-with-code--ready');

    }

    getCartLineDiscountTitles(item) {
      const directDiscounts = Array.isArray(item?.discounts)
        ? item.discounts.map((discount) => discount?.title).filter(Boolean)
        : [];

      const lineAllocations = Array.isArray(item?.line_level_discount_allocations)
        ? item.line_level_discount_allocations
          .map((allocation) => allocation?.discount_application?.title)
          .filter(Boolean)
        : [];

      return [...directDiscounts, ...lineAllocations];
    }

    getCartProductTags(productId) {
      const lineEl = document.querySelector(`[data-cart-product-id="${productId}"]`);
      const tagAttr = lineEl?.getAttribute('data-cart-product-tags');
      if (!tagAttr) return [];

      try {
        const tags = JSON.parse(tagAttr);
        return Array.isArray(tags) ? tags : [];
      } catch (error) {
        return [];
      }
    }

    updateCartDrawerTieredDiscount() {
      const target = document.getElementById('cart-drawer-tiered-discount');
      if (!target) return;

      if (!this.tdcData?.TDC_DP_cart_drawer_enable) {
        target.innerHTML = '';
        target.hidden = true;
        return;
      }

      const cartData = this.lastCartData;
      if (!cartData?.items?.length) {
        target.innerHTML = '';
        target.hidden = true;
        return;
      }

      const itemsFilter = cartData.items.filter((item) => {
        const tags = this.getCartProductTags(item.product_id).map((tag) => String(tag));
        return !tags.includes('hide-sale-price');
      });

      if (!itemsFilter.length) {
        target.innerHTML = '';
        target.hidden = true;
        return;
      }

      const discountCodeTitles = [
        this.tdcData?.TDC_DP_second_tiered_discount_code,
        this.tdcData?.TDC_DP_tiered_discount_code,
        this.tdcData?.TDC_DP_base_discount_code
      ].filter(Boolean);

      const discountCodeIsApplied = itemsFilter.some((item) => {
        const titles = this.getCartLineDiscountTitles(item);
        return titles.some((title) => discountCodeTitles.includes(title));
      });

      const discountMemberApplied = itemsFilter.some((item) => {
        const titles = this.getCartLineDiscountTitles(item);
        return titles.some((title) => String(title).toLowerCase().includes('member'));
      });

      const membershipInCart = cartData.items.some((item) => item.product_type === 'Membership');

      let bundleDiscountCalc = 0;
      itemsFilter.forEach((item) => {
        const titles = this.getCartLineDiscountTitles(item);
        if (titles.some((title) => String(title).toLowerCase().includes('bundle'))) {
          bundleDiscountCalc += (this.tdcData?.TDC_DP_bundle_discount_for_per_item || 0) * 100 * (item.quantity || 0);
        }
      });

      const initOriginalTotalPrice = (cartData.original_total_price || 0) - bundleDiscountCalc - (membershipInCart ? (this.membershipPrice || 0) : 0);
      const itemsTotalPrice = discountMemberApplied
        ? initOriginalTotalPrice * (1 - ((this.tdcData?.TDC_DP_member_discount_when_code_applied || 0) / 100))
        : initOriginalTotalPrice;

      if (itemsTotalPrice <= 0 || discountCodeIsApplied) {
        target.innerHTML = '';
        target.hidden = true;
        return;
      }

      target.innerHTML = this.renderCartDrawerDiscount(itemsTotalPrice, membershipInCart);
      target.hidden = false;
    }

    renderCartDrawerDiscount(priceCents, membershipInCart = false) {
      const { finalPrice, discountCode } = this.getFinalPriceAndDiscountCode(priceCents, {
        isCartDrawer: true,
        membershipInCart
      });

      return `
        <div class="with-code" style="display: flex; align-items: center; gap: 6px;">
          <div
            class="cart-drawer-final-price"
            style="
              color: ${this.tdcData?.TDC_DP_sale_price_font_color || '#000'};
              font-size: 18px;
              font-weight: 700;
              padding: 2px 5px;
              border-radius: 5px;
            "
          >${this.priceSymbol}${finalPrice.toFixed(2)}</div>
          <div style="display: flex; align-items: center; gap: 6px;">
            <span style="display: flex; max-width: 26px; font-size: 10px; line-height: normal;">${this.tdcData?.TDC_DP_witch_code_text_for_collection || ''}</span>
            <span
              class="cart-drawer-discount-code"
              style="
                font-size: 18px;
                font-weight: 700;
                padding: 2px 5px;
                border-radius: 5px;
                background-color: ${this.tdcData?.TDC_DP_code_background_color || '#000'};
                color: ${this.tdcData?.TDC_DP_code_font_color || '#fff'};
              "
            >${discountCode || ''}</span>
          </div>
        </div>
      `;
    }

    getFinalPriceAndDiscountCode(priceCents, options = {}) {
      const {
        isMemberPacks = false,
        isCartDrawer = false,
        membershipInCart = false,
      } = options;

      const price = this.normalizeCents(priceCents);
      const cartAmount = isCartDrawer ? price : price + this.cartTotal;

      const tdc = this.tdcData || {};
      const sdc = this.sdcData || {};

      const isTiered = cartAmount >= (tdc.TDC_DP_threshold_for_tiered_discount || 0);
      const isSecondTiered =
        Boolean(tdc.TDC_DP_second_threshold_for_tiered_discount) &&
        cartAmount >= (tdc.TDC_DP_second_threshold_for_tiered_discount || 0);

      const baseDiscount = (tdc.TDC_DP_base_discount_code_off || 0) / 100;
      const tieredDiscount = (tdc.TDC_DP_tiered_discount_code_off || 0) / 100;
      const secondTieredDiscount = (tdc.TDC_DP_second_tiered_discount_code_off || 0) / 100;

      const memberDiscount = sdc.SDC_DP_enable
        ? (sdc.SDC_DP_member_discount_when_code_applied || 0) / 100
        : (tdc.TDC_DP_member_discount_when_code_applied || 0) / 100;

      const selectedDiscount = sdc.SDC_DP_enable
        ? (sdc.SDC_DP_base_discount_code_off || 0) / 100
        : isSecondTiered
          ? secondTieredDiscount
          : isTiered
            ? tieredDiscount
            : baseDiscount;

      const discountCode = sdc.SDC_DP_enable
        ? sdc.SDC_DP_base_discount_code
        : isSecondTiered
          ? tdc.TDC_DP_second_tiered_discount_code
          : isTiered
            ? tdc.TDC_DP_tiered_discount_code
            : tdc.TDC_DP_base_discount_code;

      const discountedPrice = price * (1 - selectedDiscount);

      const finalPriceCents = isMemberPacks
        ? discountedPrice * (1 - memberDiscount) + (this.membershipPrice || 0)
        : membershipInCart
          ? discountedPrice + (this.membershipPrice || 0)
          : discountedPrice;

      return {
        finalPrice: finalPriceCents / 100,
        discountCode,
      };
    }

    normalizeCents(value) {
      const num = Number(value);
      if (Number.isNaN(num)) return 0;
      return num >= 1 && num < 10 ? Math.round(num * 100) : num;
    }

    formatMoney(raw) {
      const num = Number(raw);
      if (Number.isNaN(num)) return '0.00';
      const cents = num >= 1 && num < 10 ? Math.round(num * 100) : num;
      return (cents / 100).toFixed(2);
    }

    observeContainer(selector, callback) {
      if (!selector || typeof callback !== 'function') return;
      if (this.observers.has(selector)) return;

      const target = document.querySelector(selector);
      if (!target) return;

      const observer = new MutationObserver(() => {
        callback();
      });

      observer.observe(target, { childList: true, subtree: true });
      this.observers.set(selector, observer);
    }

    observePdpSwap() {
      if (this.observers.has('pdp-swap')) return;
      const handler = (event) => {
        const container = document.getElementById('main-content');
        if (!container) return;
        this.applyDiscounts();
      };
      document.addEventListener('pdp:swap:success', handler);
      this.observers.set('pdp-swap', handler);
    }

    observeCollectionGrid() {
      if (this.observers.has('collection-grid')) return;
      const isCollection = window.location.pathname.includes('/collections/');
      if (!isCollection) return;

      const target = document.querySelector('#ProductGridContainer') || document.querySelector('#product-grid');
      if (!target || typeof MutationObserver !== 'function') return;

      // Debounced observer: avoid loops when we update DOM inside applyDiscounts.
      const observer = new MutationObserver(() => this.scheduleCollectionApply());
      observer.observe(target, { childList: true, subtree: true });
      this.observers.set('collection-grid', observer);
    }

    observeQuickView() {
      if (this.observers.has('quick-view')) return;
      const handler = () => {
        requestAnimationFrame(() => {
          this.cartTotal = this.getInitialCartTotalFromNodes();
          this.applyDiscounts();
        });
      };
      document.addEventListener('quick-view:open', handler);
      this.observers.set('quick-view', handler);
    }

    scheduleCollectionApply() {
      if (this.collectionApplyScheduled) return;
      this.collectionApplyScheduled = true;
      requestAnimationFrame(() => {
        this.collectionApplyScheduled = false;
        this.applyDiscounts();
      });
    }

    buildProductIndex() {
      const map = { byId: {}, byHandle: {} };
      if (!Array.isArray(this.products)) return map;
      this.products.forEach((product) => {
        if (!product) return;
        const idKey = String(product.id);
        if (idKey) map.byId[idKey] = product;
        if (product.handle) map.byHandle[String(product.handle)] = product;
      });
      return map;
    }

    syncProducts() {
      if (!Array.isArray(this.products)) this.products = [];
      const nextProducts = Array.isArray(window.globalProductObject) ? window.globalProductObject : [];
      if (!nextProducts.length) return;
      if (nextProducts.length !== this.products.length) {
        this.products = nextProducts;
        this.productIndex = this.buildProductIndex();
      }
    }

    parseTagList(value) {
      if (!value) return [];
      if (Array.isArray(value)) {
        return value.map((tag) => String(tag).trim().toLowerCase()).filter(Boolean);
      }
      return String(value)
        .split(',')
        .map((tag) => tag.trim().toLowerCase())
        .filter(Boolean);
    }

    shouldRenderDiscountForProduct(product) {
      const tags = (product?.tags || []).map((tag) => String(tag).toLowerCase());
      const isExcluded = this.unlockDiscountExcludingTags.some((tag) => tags.includes(tag));
      if (isExcluded) return { render: false, isSingleDiscount: false };

      const tdcEnabled = Boolean(this.tdcData?.TDC_DP_enable);
      const sdcEnabled = Boolean(this.sdcData?.SDC_DP_enable);
      const hasInclusionTags = this.sdcInclusionTags.length > 0;
      const isSingleDiscount = sdcEnabled && hasInclusionTags && this.sdcInclusionTags.some((tag) => tags.includes(tag));

      if (isSingleDiscount) return { render: true, isSingleDiscount: true };
      if (tdcEnabled) return { render: true, isSingleDiscount: false };

      return { render: false, isSingleDiscount: false };
    }

    ensureSearchspringDiscountNodes() {
      if (this.collectionTurnOffSearchspring || !window.ShopData?.settings?.ss_enable) return;

      this.syncProducts();
      const container = this.ssContainerEl || this.getSearchspringContainer();
      if (!container) return;

      const cards = Array.from(container.querySelectorAll('.ss__result.ss__result--item'));
      if (!cards.length) return;

      cards.forEach((card) => {
        if (card.querySelector('.discount-with-code')) return;

        const product = this.getProductFromCard(card);
        if (!product) return;

        const { render, isSingleDiscount } = this.shouldRenderDiscountForProduct(product);
        if (!render) return;

        const priceEl = card.querySelector('.product-card-info .price');
        if (priceEl) priceEl.classList.add('is-hidden');

        const target = card.querySelector('.product-card-info') || card;
        const node = this.buildDiscountNode(product, isSingleDiscount);
        target.appendChild(node);
        this.updateProductNode(node);
      });
    }

    getSearchspringContainer() {
      return document.querySelector('#ss__content--products') || document.querySelector('#product-grid');
    }

    waitForSearchspringContainer() {
      if (this.ssContainerObserver || typeof MutationObserver !== 'function') return;

      // Searchspring injects the container after scripts load.
      this.ssContainerObserver = new MutationObserver(() => {
        const container = this.getSearchspringContainer();
        if (!container) return;
        this.ssContainerObserver.disconnect();
        this.ssContainerObserver = null;
        this.initSearchspringContainer(container);
      });

      this.ssContainerObserver.observe(document.body, { childList: true, subtree: true });
    }

    observeSearchspringContainer(container) {
      if (this.ssCardsObserver || typeof MutationObserver !== 'function') return;
      if (!container) return;

      // Re-run when Searchspring swaps results.
      this.ssCardsObserver = new MutationObserver((mutations) => {
        const shouldRun = mutations.some((m) => {
          if (!m.addedNodes.length && !m.removedNodes.length) return false;

          const nodes = [...m.addedNodes, ...m.removedNodes];
          return nodes.some((node) => {
            if (!(node instanceof HTMLElement)) return false;
            if (node.classList?.contains('discount-with-code') || node.closest?.('.discount-with-code')) return false;
            return node.matches?.('.ss__result') || Boolean(node.querySelector?.('.ss__result'));
          });
        });

        if (shouldRun) this.scheduleSearchspringRun();
      });

      this.ssCardsObserver.observe(container, { childList: true, subtree: true });
    }

    startSearchspring() {
      if (this.ssInitialized || this.collectionTurnOffSearchspring || !window.ShopData?.settings?.ss_enable) return;
      this.ssInitialized = true;

      if (this.ssInitTimeout) clearTimeout(this.ssInitTimeout);
      this.ssInitTimeout = setTimeout(() => {
        const container = this.getSearchspringContainer();
        if (container) {
          this.initSearchspringContainer(container);
        } else {
          this.waitForSearchspringContainer();
        }
      }, 1200);
    }

    initSearchspringContainer(container) {
      this.ssContainerEl = container;
      this.observeSearchspringContainer(container);
      this.scheduleSearchspringRun();
    }

    scheduleSearchspringRun() {
      if (this.ssPending) return;
      this.ssPending = true;
      requestAnimationFrame(() => {
        this.ssPending = false;
        this.ensureSearchspringDiscountNodes();
      });
    }

    getProductFromCard(card) {
      const idAttr = card.getAttribute('id') || '';
      const idMatch = idAttr.match(/ss__result--products-(\d+)/i);
      if (idMatch && idMatch[1]) {
        return this.productIndex.byId[idMatch[1]]
          || this.products.find((p) => String(p.id) === String(idMatch[1]))
          || null;
      }

      const quickView = card.querySelector('.product-card-quickview[data-product-handle]');
      const handle = quickView?.getAttribute('data-product-handle');
      if (handle && this.productIndex.byHandle[handle]) return this.productIndex.byHandle[handle];
      if (handle) {
        const found = this.products.find((p) => String(p.handle) === String(handle));
        if (found) return found;
      }

      const link = card.querySelector('a[href*="/products/"]');
      if (link) {
        const href = link.getAttribute('href') || '';
        const match = href.match(/\/products\/([^/?#]+)/i);
        if (match && this.productIndex.byHandle[match[1]]) return this.productIndex.byHandle[match[1]];
        if (match) {
          const found = this.products.find((p) => String(p.handle) === String(match[1]));
          if (found) return found;
        }
      }

      return null;
    }

    buildDiscountNode(product, isSingleDiscount) {
      const styles = isSingleDiscount ? this.sdcData : this.tdcData;
      const saleColor = styles?.SDC_DP_sale_price_font_color || styles?.TDC_DP_sale_price_font_color || '#000';
      const codeBg = styles?.SDC_DP_code_background_color || styles?.TDC_DP_code_background_color || '#000';
      const codeColor = styles?.SDC_DP_code_font_color || styles?.TDC_DP_code_font_color || '#fff';
      const text = styles?.SDC_DP_witch_code_text_for_product || styles?.TDC_DP_witch_code_text_for_product || '';

      const compareAt = product.compare_at_price || product.price || 0;
      const wrapper = document.createElement('div');
      wrapper.className = `product-price-container discount-with-code${isSingleDiscount ? ' is-single-discount' : ''}`;
      wrapper.dataset.cartTotalPrice = String(this.cartTotal || 0);
      wrapper.dataset.pId = String(product.id || '');
      wrapper.dataset.pPrice = String(product.price || 0);
      wrapper.dataset.pCompareAtPrice = String(compareAt || product.price || 0);

      wrapper.innerHTML = `
        <span class="discount-compare-at-price" style="margin-right: 5px; font-weight: 600; color: #7f7f7f; text-decoration: line-through;"></span>
        <span class="discount-price" style="margin-right: 5px; font-weight: 600; color: ${saleColor}"></span>
        <span class="discount-text" style="margin-right: 5px; font-size: 12px">${text}</span>
        <span
          class="discount-code"
          style="
            width: fit-content;
            display: inline-flex;
            min-height: 16px;
            align-items: center;
            padding: 2px 8px;
            border-radius: 2px;
            font-weight: 600;
            font-size: 12px;
            text-align: center;
            background-color: ${codeBg};
            color: ${codeColor};
          "
        ></span>
      `;

      return wrapper;
    }
  }

  window.TieredAndSingleDiscountCode = new TieredAndSingleDiscountCode();
})();
